import { useEffect, useState } from "react";
import { Car, ShieldCheck, TrendingUp } from "lucide-react";
import { useCountUp } from "@/lib/useCountUp";
import { api } from "@/lib/api";
import { useMileage } from "@/context/MileageContext";

// ============================================================================
// Mileage Deduction Odometer — reads live values from MileageContext (instant
// sync when Enterprise MileageTab logs / edits / deletes a trip) and keeps an
// internal 15s API poll to stay in sync for tripCount + businessDed stats.
// ============================================================================
export function MileageDeductionOdometer() {
  const { totalMiles: ctxMiles, totalDeduction: ctxDed, refresh: refreshCtx } = useMileage();
  const [tripCount, setTripCount] = useState(0);
  const [businessDed, setBusinessDed] = useState(0);
  // Rolling animation target follows context (instant nudge) or API fallback
  const rolledDed = useCountUp(ctxDed, 1200);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/mileage");
        if (cancelled) return;
        // Sync context with authoritative API values
        refreshCtx();
        setTripCount((data?.trips || []).length);
        setBusinessDed(Number(data?.business_mileage_deduction || 0));
      } catch {
        // silent
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [refreshCtx]);

  return (
    <div
      data-testid="mileage-deduction-odometer-card"
      className="glass rounded-2xl p-6 relative overflow-hidden"
      style={{
        borderColor: "rgba(103,232,249,0.35)",
        background: "linear-gradient(180deg, rgba(2,6,23,0.7), rgba(6,182,212,0.06))",
      }}
    >
      <span
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(103,232,249,0.28), transparent 65%)",
          filter: "blur(20px)",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 flex items-center gap-1.5">
            <Car size={12} /> Mileage Deduction · Live Ticker
          </div>
          <span
            data-testid="mileage-audit-safe-chip"
            className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 audit-safe-glow"
            title="Every trip is anchored to IRC § 162 + § 274(d)"
          >
            <ShieldCheck size={9} /> Audit-Safe
          </span>
        </div>

        <div
          data-testid="mileage-deduction-odometer-value"
          className="text-4xl sm:text-5xl font-mono font-bold tracking-tight"
          style={{
            background: "linear-gradient(90deg, #67e8f9, #fde047)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: "drop-shadow(0 0 6px rgba(103,232,249,0.4))",
          }}
        >
          ${Number(rolledDed).toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Total logged deduction · IRS 2026 rates
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 border-t border-white/5 pt-4">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Trips</div>
            <div className="text-white text-lg font-mono font-bold" data-testid="mileage-trip-count">{tripCount}</div>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Miles</div>
            <div className="text-white text-lg font-mono font-bold" data-testid="mileage-total-miles">
              {Number(ctxMiles).toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <TrendingUp size={9} /> Biz →401(k)
            </div>
            <div className="text-emerald-300 text-lg font-mono font-bold" data-testid="mileage-business-pipeline">
              ${Number(businessDed).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Business mileage folds into Retirement Runway automatically
        </div>
      </div>
    </div>
  );
}

export default MileageDeductionOdometer;
